/**
 * Support Center component for multi-tenant admin customer support management
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Grid,
  Divider,
  Stack,
} from '@mui/material';
import {
  Support as SupportIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useAdmin } from '../../contexts/AdminContext';
import { SupportTicket, SupportTicketFilters, SupportTicketMessage } from '../../types/admin';
import { formatDate, formatNumber } from '../../utils/formatters';

interface SupportCenterProps {
  onNavigate?: (path: string) => void;
}

type DialogType = 'assign' | 'update' | 'view';

const SupportCenter: React.FC<SupportCenterProps> = () => {
  const {
    supportTickets,
    supportTicketsLoading,
    supportTicketsError,
    loadSupportTickets,
    updateSupportTicket,
    assignSupportTicket,
    respondToTicket,
    getTicketMessages,
    adminUsers,
    loadAdminUsers,
    clearError,
  } = useAdmin();

  const [filters, setFilters] = useState<SupportTicketFilters>({
    page: 1,
    page_size: 20,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    type: DialogType;
    ticket: SupportTicket | null;
  }>({
    open: false,
    type: 'view',
    ticket: null,
  });
  const [analytics, setAnalytics] = useState<{
    totalTickets: number;
    openTickets: number;
    resolvedTickets: number;
    resolutionRate: number;
  } | null>(null);

  // Editable form state for the action dialog
  const [formStatus, setFormStatus] = useState<SupportTicket['status']>('open');
  const [formPriority, setFormPriority] = useState<SupportTicket['priority']>('medium');
  const [formResolution, setFormResolution] = useState('');
  const [assigneeId, setAssigneeId] = useState<string>('');
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Message thread state (view dialog)
  const [messages, setMessages] = useState<SupportTicketMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [replyInternal, setReplyInternal] = useState(false);
  const [replySubmitting, setReplySubmitting] = useState(false);

  const loadAnalytics = useCallback(() => {
    const totalTickets = supportTickets.length;
    const openTickets = supportTickets.filter((t) => t.status === 'open').length;
    const resolvedTickets = supportTickets.filter((t) => t.status === 'resolved').length;
    setAnalytics({
      totalTickets,
      openTickets,
      resolvedTickets,
      resolutionRate: totalTickets > 0 ? (resolvedTickets / totalTickets) * 100 : 0,
    });
  }, [supportTickets]);

  useEffect(() => {
    loadSupportTickets(filters);
  }, [loadSupportTickets, filters]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  // Admin users power the "assign to" dropdown
  useEffect(() => {
    loadAdminUsers();
  }, [loadAdminUsers]);

  const adminUserLabel = useCallback(
    (assignedTo?: string | null): string => {
      if (!assignedTo) return 'Unassigned';
      const match = adminUsers.find((au) => String(au.id) === String(assignedTo));
      if (match) {
        return `${match.admin_role} (user #${match.user_id})`;
      }
      return `Admin ${String(assignedTo).slice(0, 8)}`;
    },
    [adminUsers]
  );

  const handleFilterChange = (key: keyof SupportTicketFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, ticket: SupportTicket) => {
    setAnchorEl(event.currentTarget);
    setSelectedTicket(ticket);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTicket(null);
  };

  const openDialog = (type: DialogType, ticket: SupportTicket) => {
    setActionError(null);
    setFormStatus(ticket.status);
    setFormPriority(ticket.priority);
    setFormResolution(ticket.resolution || '');
    setAssigneeId(ticket.assigned_to ? String(ticket.assigned_to) : '');
    setMessages([]);
    setReplyContent('');
    setReplyInternal(false);
    setActionDialog({ open: true, type, ticket });
  };

  const handleAction = (type: DialogType) => {
    if (selectedTicket) {
      openDialog(type, selectedTicket);
    }
    handleMenuClose();
  };

  // Load message thread when the view dialog opens
  const loadMessages = useCallback(
    async (ticketId: string) => {
      try {
        setMessagesLoading(true);
        const data = await getTicketMessages(ticketId);
        setMessages(data);
      } catch (error) {
        console.error('Error loading ticket messages:', error);
      } finally {
        setMessagesLoading(false);
      }
    },
    [getTicketMessages]
  );

  useEffect(() => {
    if (actionDialog.open && actionDialog.type === 'view' && actionDialog.ticket) {
      loadMessages(actionDialog.ticket.id);
    }
  }, [actionDialog.open, actionDialog.type, actionDialog.ticket, loadMessages]);

  const handleActionConfirm = async () => {
    if (!actionDialog.ticket) return;
    setActionError(null);
    try {
      setActionSubmitting(true);
      if (actionDialog.type === 'assign') {
        await assignSupportTicket(actionDialog.ticket.id, assigneeId || null);
      } else if (actionDialog.type === 'update') {
        await updateSupportTicket(actionDialog.ticket.id, {
          status: formStatus,
          priority: formPriority,
          resolution: formResolution || undefined,
        });
      }
      setActionDialog({ open: false, type: 'view', ticket: null });
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Action failed. Please try again.');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleSendReply = async () => {
    if (!actionDialog.ticket || !replyContent.trim()) return;
    setActionError(null);
    try {
      setReplySubmitting(true);
      await respondToTicket(actionDialog.ticket.id, replyContent.trim(), replyInternal);
      setReplyContent('');
      setReplyInternal(false);
      await loadMessages(actionDialog.ticket.id);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Failed to send reply.');
    } finally {
      setReplySubmitting(false);
    }
  };

  const handleActionCancel = () => {
    setActionDialog({ open: false, type: 'view', ticket: null });
    setActionError(null);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'error';
      case 'in_progress':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'bug_report':
        return 'error';
      case 'technical':
        return 'warning';
      case 'billing':
        return 'info';
      case 'feature_request':
        return 'success';
      default:
        return 'default';
    }
  };

  // Client-side search across subject/description
  const visibleTickets = supportTickets.filter((t) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      t.subject.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q) ||
      (t.ticket_number || '').toLowerCase().includes(q)
    );
  });

  if (supportTicketsLoading && !supportTickets.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (supportTicketsError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={clearError}>
            Dismiss
          </Button>
        }
      >
        {supportTicketsError}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" display="flex" alignItems="center" gap={1}>
          <SupportIcon />
          Support Center
        </Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => loadSupportTickets(filters)} disabled={supportTicketsLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Analytics Cards */}
      {analytics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <SupportIcon color="primary" />
                  <Typography variant="h6">{formatNumber(analytics.totalTickets)}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Total Tickets
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <CancelIcon color="error" />
                  <Typography variant="h6">{formatNumber(analytics.openTickets)}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Open Tickets
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <CheckCircleIcon color="success" />
                  <Typography variant="h6">{formatNumber(analytics.resolvedTickets)}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Resolved Tickets
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <TrendingUpIcon color="primary" />
                  <Typography variant="h6">{analytics.resolutionRate.toFixed(1)}%</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Resolution Rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              label="Search tickets"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status || ''}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={filters.priority || ''}
                label="Priority"
                onChange={(e) => handleFilterChange('priority', e.target.value || undefined)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category || ''}
                label="Category"
                onChange={(e) => handleFilterChange('category', e.target.value || undefined)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="billing">Billing</MenuItem>
                <MenuItem value="technical">Technical</MenuItem>
                <MenuItem value="feature_request">Feature Request</MenuItem>
                <MenuItem value="bug_report">Bug Report</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => loadSupportTickets(filters)}
              disabled={supportTicketsLoading}
            >
              Apply Filters
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Support Tickets ({formatNumber(visibleTickets.length)})
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ticket</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleTickets.map((ticket) => (
                  <TableRow key={ticket.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {ticket.ticket_number || `#${String(ticket.id).slice(0, 8)}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {ticket.subject}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ticket.category.replace(/_/g, ' ')}
                        color={getCategoryColor(ticket.category) as any}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ticket.priority.toUpperCase()}
                        color={getPriorityColor(ticket.priority) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ticket.status.replace(/_/g, ' ').toUpperCase()}
                        color={getStatusColor(ticket.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">User #{ticket.user_id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{adminUserLabel(ticket.assigned_to)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(ticket.created_at)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, ticket)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleAction('view')}>
          <ListItemIcon>
            <SupportIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View &amp; Reply</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAction('assign')}>
          <ListItemIcon>
            <AssignmentIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Assign Ticket</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAction('update')}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Update Status</ListItemText>
        </MenuItem>
      </Menu>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onClose={handleActionCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          {actionDialog.type === 'assign' && 'Assign Ticket'}
          {actionDialog.type === 'update' && 'Update Ticket'}
          {actionDialog.type === 'view' && 'Ticket Details'}
        </DialogTitle>
        <DialogContent dividers>
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          )}

          {actionDialog.ticket && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Ticket
                  </Typography>
                  <Typography variant="body1">
                    {actionDialog.ticket.ticket_number || `#${String(actionDialog.ticket.id).slice(0, 8)}`}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={actionDialog.ticket.status.replace(/_/g, ' ').toUpperCase()}
                    color={getStatusColor(actionDialog.ticket.status) as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Subject
                  </Typography>
                  <Typography variant="body1">{actionDialog.ticket.subject}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">{actionDialog.ticket.description}</Typography>
                </Grid>
              </Grid>

              {/* Assign form */}
              {actionDialog.type === 'assign' && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  <FormControl fullWidth>
                    <InputLabel id="assignee-label">Assign to</InputLabel>
                    <Select
                      labelId="assignee-label"
                      label="Assign to"
                      value={assigneeId}
                      onChange={(e) => setAssigneeId(e.target.value)}
                    >
                      <MenuItem value="">Unassigned</MenuItem>
                      {adminUsers.map((au) => (
                        <MenuItem key={String(au.id)} value={String(au.id)}>
                          {au.admin_role} (user #{au.user_id})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {adminUsers.length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      No admin users found to assign. You can still unassign the ticket.
                    </Typography>
                  )}
                </Box>
              )}

              {/* Update form */}
              {actionDialog.type === 'update' && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <FormControl fullWidth>
                        <InputLabel id="status-label">Status</InputLabel>
                        <Select
                          labelId="status-label"
                          label="Status"
                          value={formStatus}
                          onChange={(e) => setFormStatus(e.target.value as SupportTicket['status'])}
                        >
                          <MenuItem value="open">Open</MenuItem>
                          <MenuItem value="in_progress">In Progress</MenuItem>
                          <MenuItem value="resolved">Resolved</MenuItem>
                          <MenuItem value="closed">Closed</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl fullWidth>
                        <InputLabel id="priority-label">Priority</InputLabel>
                        <Select
                          labelId="priority-label"
                          label="Priority"
                          value={formPriority}
                          onChange={(e) => setFormPriority(e.target.value as SupportTicket['priority'])}
                        >
                          <MenuItem value="low">Low</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                          <MenuItem value="urgent">Urgent</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                    <TextField
                      label="Resolution notes"
                      value={formResolution}
                      onChange={(e) => setFormResolution(e.target.value)}
                      fullWidth
                      multiline
                      minRows={3}
                      placeholder="Add resolution details (shown when the ticket is resolved/closed)."
                    />
                  </Stack>
                </Box>
              )}

              {/* View: message thread + reply */}
              {actionDialog.type === 'view' && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Conversation
                  </Typography>

                  {messagesLoading ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : messages.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No replies yet.
                    </Typography>
                  ) : (
                    <Stack spacing={1.5} sx={{ mb: 2 }}>
                      {messages.map((msg) => (
                        <Paper
                          key={msg.id}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            bgcolor: msg.sender_type === 'admin' ? 'action.hover' : 'background.paper',
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip
                                label={msg.sender_type === 'admin' ? 'Support' : 'Customer'}
                                size="small"
                                color={msg.sender_type === 'admin' ? 'primary' : 'default'}
                              />
                              {msg.is_internal && (
                                <Chip label="Internal note" size="small" color="warning" variant="outlined" />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(msg.created_at)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {msg.message_content}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  )}

                  <TextField
                    label="Write a reply"
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                    disabled={replySubmitting}
                    placeholder="Type your response to the customer…"
                  />
                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={replyInternal}
                          onChange={(e) => setReplyInternal(e.target.checked)}
                          disabled={replySubmitting}
                        />
                      }
                      label="Internal note (not shown to customer)"
                    />
                    <Button
                      variant="contained"
                      startIcon={
                        replySubmitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />
                      }
                      onClick={handleSendReply}
                      disabled={replySubmitting || !replyContent.trim()}
                    >
                      Send Reply
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleActionCancel}>{actionDialog.type === 'view' ? 'Close' : 'Cancel'}</Button>
          {actionDialog.type !== 'view' && (
            <Button
              onClick={handleActionConfirm}
              variant="contained"
              color="primary"
              disabled={actionSubmitting}
              startIcon={actionSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {actionDialog.type === 'assign' && 'Assign'}
              {actionDialog.type === 'update' && 'Save Changes'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportCenter;
